import React, { useRef } from 'react';
import { Box, Typography, Button, Chip, Alert } from '@mui/material';
import {
  AccountTree as DiagramIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  Image as ImageIcon,
} from '@mui/icons-material';

const ACCEPTED_TYPES = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/svg+xml': '.svg',
};

const MAX_SIZE_MB = 10;

/**
 * Optional architecture diagram upload section.
 * The uploaded image is embedded into Section 4 of the generated SOW document.
 */
const ArchitectureDiagramUpload = ({ uploadedDiagram, uploadError, onFileSelect, onFileClear }) => {
  const inputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndSelect(file);
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) validateAndSelect(file);
    e.target.value = '';
  };

  const validateAndSelect = (file) => {
    if (!ACCEPTED_TYPES[file.type]) {
      onFileSelect(null, 'Unsupported file type. Please upload a PNG, JPG, or SVG image.');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      onFileSelect(null, `File is too large. Maximum size is ${MAX_SIZE_MB} MB.`);
      return;
    }
    onFileSelect(file);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Show image preview for raster images
  const previewUrl = uploadedDiagram?.file && uploadedDiagram.file.type !== 'image/svg+xml'
    ? URL.createObjectURL(uploadedDiagram.file)
    : null;

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
          Architecture Diagram{' '}
          <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
            (Optional)
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload your architecture diagram to embed it in <strong>Section 4: Solution Architecture</strong> of the SOW.
          Supported formats: <strong>.png</strong>, <strong>.jpg</strong>, <strong>.svg</strong> — max {MAX_SIZE_MB} MB.
        </Typography>
      </Box>

      {uploadError && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }} onClose={onFileClear}>
          {uploadError}
        </Alert>
      )}

      {uploadedDiagram?.file ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'success.light',
            background: 'rgba(180, 228, 206, 0.12)',
          }}
        >
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt="Architecture diagram preview"
              sx={{ width: 80, height: 60, objectFit: 'contain', borderRadius: 1, border: '1px solid #e5e5e5', flexShrink: 0 }}
            />
          ) : (
            <DiagramIcon color="success" sx={{ mt: 0.5 }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {uploadedDiagram.file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatSize(uploadedDiagram.file.size)} · Will be embedded in Section 4
            </Typography>
          </Box>
          <Chip
            label="Remove"
            size="small"
            icon={<CloseIcon fontSize="small" />}
            onClick={onFileClear}
            sx={{ cursor: 'pointer', flexShrink: 0 }}
          />
        </Box>
      ) : (
        <Box
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => inputRef.current?.click()}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: 3,
            borderRadius: 2,
            border: '2px dashed',
            borderColor: 'rgba(89, 92, 234, 0.3)',
            background: 'rgba(89, 92, 234, 0.02)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              background: 'rgba(89, 92, 234, 0.05)',
            },
          }}
        >
          <ImageIcon sx={{ fontSize: 36, color: 'rgba(89, 92, 234, 0.5)' }} />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Drag & drop your architecture diagram, or{' '}
            <Typography component="span" color="primary" fontWeight={600}>
              click to browse
            </Typography>
          </Typography>
          <Typography variant="caption" color="text.disabled">
            PNG · JPG · SVG — max {MAX_SIZE_MB} MB
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DiagramIcon />}
            sx={{ mt: 0.5, borderRadius: 2, textTransform: 'none' }}
            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
          >
            Choose Diagram
          </Button>
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.svg"
        style={{ display: 'none' }}
        onChange={handleChange}
      />
    </Box>
  );
};

export default ArchitectureDiagramUpload;
